import { useEffect, useState, useRef } from 'react';
import { Zap, History, Plus, Search, Grid, Image, Home } from 'lucide-react';
import {
  PromptInput,
  LoadingIndicator,
  PromptFeatures,
  ModelSelector,
  SettingsTabs,
  ImageGrid,
  GenerateButton,
} from './features/ai-models';
import { useAIGenerationStore } from './store/aiGenerationStore';
import { useAuthStore } from './store/authStore';
import { UserMenu } from './components/auth';
import { ToastContainer, ImageGridSkeleton } from './components/ui';
import { useToast } from './hooks/useToast';
import { initializeDebugTools } from './utils/debugDatabase';
import { TemplateShowcase } from './components/home';
import { SceneTemplateService } from './services/business';
import { scenePackIntegration } from './services/business/scenePackIntegrationService';
import type { GenerationResult } from './types';
import type { SceneTemplate } from './types/database';

function App() {
  const {
    currentGeneration,
    generationHistory,
    generationBatches,
    usageStats,
    isLoading,
    currentConfig,
    pagination,
    updateUsageStats,
    loadHistoryFromDatabase,
    loadHistoryWithPagination,
    loadMoreHistory,
    resetPagination,
    prepareRegeneration,
    resetGeneration,
    clearHistory
  } = useAIGenerationStore();
  const [showSettings, setShowSettings] = useState(false);
  const [viewMode, setViewMode] = useState<'home' | 'gallery' | 'create'>('home');
  const [searchPrompt, setSearchPrompt] = useState('');
  const [sidebarPrompt, setSidebarPrompt] = useState(''); // 专门用于右侧栏的提示词
  const [suggestedTags, setSuggestedTags] = useState<any>(null); // 推荐的标签组合
  const [galleryLoaded, setGalleryLoaded] = useState(false); // 标记画廊数据是否已加载
  const [galleryError, setGalleryError] = useState<string | null>(null); // 画廊加载错误状态
  const [isGalleryLoading, setIsGalleryLoading] = useState(false); // 画廊独立加载状态
  const [currentPrompt, setCurrentPrompt] = useState(''); // 当前输入的提示词（用于统一生成按钮）
  const [isProcessing, setIsProcessing] = useState(false); // AI处理状态（分析、优化等）
  const [selectedScenePackId, setSelectedScenePackId] = useState<string | null>(null); // 从首页选中的场景包ID
  const [currentTab, setCurrentTab] = useState<'model' | 'prompt' | 'advanced'>('model'); // 当前tab

  // 用于取消画廊加载请求的AbortController
  const galleryAbortControllerRef = useRef<AbortController | null>(null);

  // Toast 通知系统
  const toast = useToast();

  // 调试：监控currentPrompt变化
  useEffect(() => {
    console.log('📊 App.tsx - currentPrompt changed:', currentPrompt);
  }, [currentPrompt]);

  // 调试：监控currentTab变化
  useEffect(() => {
    console.log('📊 App.tsx - currentTab changed:', currentTab);
  }, [currentTab]);

  // 认证状态
  const {
    initialize: initializeAuth,
    handleAuthCallback,
    isLoading: authLoading,
    appUser
  } = useAuthStore();

  // 初始化调试工具
  useEffect(() => {
    return initializeDebugTools();
  }, []);

  // 初始化认证和处理 OAuth 回调
  useEffect(() => {
    const initAuth = async () => {
      // 检查是否是 OAuth 回调
      const isCallback = window.location.pathname === '/auth/callback';

      if (isCallback) {
        try {
          await handleAuthCallback();
          // 回调处理完成后，重定向到首页
          window.history.replaceState({}, '', '/');
        } catch (error) {
          console.error('OAuth 回调处理失败:', error);
          window.history.replaceState({}, '', '/');
        }
      } else {
        // 正常初始化认证状态
        await initializeAuth();
      }
    };

    initAuth();
  }, [initializeAuth, handleAuthCallback]);

  // 初始化应用数据 - 只更新使用统计，画廊数据懒加载
  useEffect(() => {
    // 等待认证完成且有用户信息后才更新统计
    if (authLoading || !appUser) {
      return;
    }

    const initializeApp = async () => {
      try {
        // 只更新使用统计
        await updateUsageStats();
      } catch (error) {
        console.error('❌ 使用统计更新失败:', error);
        toast.error('使用统计加载失败', '请刷新页面重试');
      }
    };

    initializeApp();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, appUser?.id]);

  // 懒加载画廊数据 - 只有切换到画廊视图时才加载
  useEffect(() => {
    // 只有在画廊视图且未加载过数据时才加载
    // 移除了isLoading依赖，使用独立的isGalleryLoading状态
    if (viewMode !== 'gallery' || galleryLoaded || authLoading || !appUser || isGalleryLoading) {
      return;
    }

    // 使用cancelled标志防止组件卸载后的状态更新
    let cancelled = false;

    const loadGalleryData = async () => {
      // 取消之前的请求（如果存在）
      if (galleryAbortControllerRef.current) {
        galleryAbortControllerRef.current.abort();
      }

      // 创建新的AbortController
      galleryAbortControllerRef.current = new AbortController();

      if (cancelled) return;

      setIsGalleryLoading(true);
      setGalleryError(null); // 清除之前的错误

      try {
        console.log('🔄 开始加载画廊数据...');
        await loadHistoryWithPagination(1, true);

        if (!cancelled) {
          setGalleryLoaded(true);
          setGalleryError(null);
          console.log('✅ 画廊数据加载成功');
        }
      } catch (error: any) {
        // 如果是请求被取消，不显示错误
        if (error.name === 'AbortError') {
          console.log('🚫 画廊加载请求已取消');
          return;
        }

        if (!cancelled) {
          const errorMessage = error?.message || '未知错误';
          console.error('❌ 画廊数据加载失败:', errorMessage);
          setGalleryError(errorMessage);
          // 不设置galleryLoaded，允许用户重试
          toast.error('画廊加载失败', '请检查网络连接后重试');
        }
      } finally {
        if (!cancelled) {
          setIsGalleryLoading(false);
        }
      }
    };

    loadGalleryData();

    // 清理函数：组件卸载或依赖变化时取消请求
    return () => {
      cancelled = true;
      if (galleryAbortControllerRef.current) {
        galleryAbortControllerRef.current.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, galleryLoaded, authLoading, appUser?.id]);

  // 监听生成完成，重置画廊加载状态
  useEffect(() => {
    if (currentGeneration.stage === 'completed') {
      // 生成完成时重置画廊加载状态，确保下次进入画廊能看到新图片
      setGalleryLoaded(false);
    }
  }, [currentGeneration.stage]);

  // 处理搜索框生成（简化版，主要通过PromptInput处理）
  const handleSearchGenerate = () => {
    if (!searchPrompt.trim()) return;
    // 将搜索框内容设置到右侧栏，然后打开设置面板
    setSidebarPrompt(searchPrompt);
    setSuggestedTags(null); // 搜索框输入不使用推荐标签
    setShowSettings(true);
  };

  // 处理模板点击（支持场景包、数据库模板、硬编码模板）
  const handleTemplateClick = async (template: any | SceneTemplate) => {
    console.log('🎯 handleTemplateClick called:', template.id || template.name);

    // 重置tab到模型配置（确保每次都从模型配置开始）
    console.log('🔄 重置tab到model');
    setCurrentTab('model');

    // 清空之前的提示词状态
    console.log('🔄 清空currentPrompt');
    setCurrentPrompt('');

    // 保存选中的场景包ID（用于在快速模式中自动选中）
    if (template.id) {
      console.log('🔄 设置selectedScenePackId:', template.id);
      setSelectedScenePackId(template.id);
    }

    // 立即打开设置面板，避免卡顿
    console.log('🔄 打开设置面板');
    setShowSettings(true);

    // 异步处理其他逻辑
    try {
      // 使用集成服务统一处理场景包和模板（已升级为完整应用逻辑）
      const result = await scenePackIntegration.applyItem(template);

      // 设置侧边栏提示词（使用基础提示词，完整提示词会在生成时构建）
      setSidebarPrompt(result.basePrompt);
      setSuggestedTags(result.suggestedTags);

      // 应用完整的生成配置（包括模型、宽高比、步数等）
      const { updateConfig } = useAIGenerationStore.getState();
      updateConfig(result.config);

      // 显示应用成功的提示
      const sourceName = result.source === 'scene_pack' ? '场景包' : '模板';
      const templateName = result.config.scenePackName || result.config.templateName || '未知';
      toast.success('应用成功', `已应用${sourceName}：${templateName}`);

      // 如果有警告信息，也显示出来
      if (result.warnings.length > 0) {
        console.warn('⚠️  场景包应用警告:', result.warnings);
      }

      // 记录场景包使用（异步，不阻塞UI）
      if (result.source === 'scene_pack' && result.config.scenePackId && appUser) {
        scenePackIntegration.trackScenePackUsage(
          result.config.scenePackId,
          appUser.id,
          {
            appliedConfig: result.config,
          }
        ).catch(err => console.error('记录场景包使用失败:', err));
      }
    } catch (error) {
      console.error('应用模板失败:', error);
      toast.error('模板加载失败', '请重试或选择其他模板');
    }
  };

  // 处理右下角+号点击
  const handleFloatingButtonClick = () => {
    setSidebarPrompt(''); // 清空右侧栏提示词
    setSuggestedTags(null); // 清空推荐标签
    setSelectedScenePackId(null); // 清空选中的场景包
    setCurrentTab('model'); // 重置到模型配置tab
    setShowSettings(!showSettings);
  };

  // 处理导航切换
  const handleNavigationChange = (newMode: 'home' | 'gallery') => {
    setViewMode(newMode);
    setShowSettings(false); // 切换页面时关闭设置面板
  };

  // 处理关闭设置面板
  const handleCloseSettings = () => {
    // 如果当前生成已完成，清空生成历史，避免下次打开时显示旧结果
    if (currentGeneration.stage === 'completed' && !currentGeneration.isGenerating) {
      resetGeneration();
      clearHistory();
    }
    setShowSettings(false);
  };

  // 处理批次重新生成
  const handleRegenerate = async (batch: any) => {
    try {
      // 从批次中构造一个GenerationResult对象用于prepareRegeneration
      const result: GenerationResult = {
        id: `${batch.id}-regenerate`,
        imageUrl: batch.results[0]?.imageUrl || '',
        prompt: batch.prompt,
        config: batch.config,
        createdAt: batch.createdAt,
        status: 'completed',
      };

      // 准备重新生成配置
      await prepareRegeneration(result);

      // 获取更新后的配置，使用解析出的基础提示词
      const { currentConfig: updatedConfig } = useAIGenerationStore.getState();

      // 设置侧边栏提示词（使用解析出的基础描述，而不是完整的技术标签）
      setSidebarPrompt(updatedConfig.prompt || batch.prompt);
      setSuggestedTags(null); // 重新生成不使用推荐标签

      // 打开设置面板
      setShowSettings(true);

    } catch (error) {
      console.error('❌ 准备重新生成失败:', error);
      toast.error('准备重新生成失败', '请重试或联系客服');
    }
  };

  // 检查是否有内容（使用generationBatches）
  const hasContent = generationBatches.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部固定导航栏 - 类似现代创作工具 */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            
            {/* 左侧品牌 */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Prism</h1>
                  <div className="text-xs text-gray-500">AI创作平台</div>
                </div>
              </div>
            </div>

            {/* 中间搜索/生成区域 */}
            <div className="flex-1 max-w-2xl mx-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="描述你想要生成的图像..."
                  value={searchPrompt}
                  onChange={(e) => setSearchPrompt(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchGenerate();
                    }
                  }}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
                <button 
                  onClick={handleSearchGenerate}
                  disabled={!searchPrompt.trim()}
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-xl transition-colors ${
                    !searchPrompt.trim()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700'
                  } text-white`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 右侧工具栏 */}
            <div className="flex items-center space-x-3">
              {/* 用量显示 - 使用 appUser 数据保持与 UserMenu 一致 */}
              {appUser && (
                <div className="hidden lg:flex items-center space-x-2 text-sm bg-gray-100 px-3 py-2 rounded-xl">
                  <div className={`w-2 h-2 rounded-full ${
                    (appUser.daily_quota - appUser.used_today) > 0 ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <span className="text-gray-600">{appUser.daily_quota - appUser.used_today}/{appUser.daily_quota}</span>
                </div>
              )}
              
              {/* 导航切换 */}
              <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-xl">
                <button
                  onClick={() => handleNavigationChange('home')}
                  className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                    viewMode === 'home' ? 'bg-white shadow-sm text-purple-600' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title="首页"
                >
                  <Home className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleNavigationChange('gallery')}
                  className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                    viewMode === 'gallery' ? 'bg-white shadow-sm text-purple-600' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title="我的作品"
                >
                  <Image className="w-4 h-4" />
                </button>
              </div>

              {/* 用户菜单 */}
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="pt-20">

        {/* 首页视图 - 使用CSS隐藏而不是卸载，保留组件状态和缓存 */}
        <div
          className={`max-w-7xl mx-auto px-6 py-12 ${
            viewMode === 'home' ? 'block animate-fade-in' : 'hidden'
          }`}
        >
          {/* Hero区域 */}
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl mx-auto mb-6 flex items-center justify-center">
              <Zap className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              用AI创造无限可能
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              从 25+ 专业模板中选择，或输入你的创意想法，让AI为你生成令人惊艳的图像作品
            </p>
          </div>

          {/* 新的模板展示组件 */}
          <TemplateShowcase
            onSelectTemplate={handleTemplateClick}
            selectedTemplateId={undefined}
          />

            {/* 用户作品统计（如果有） */}
            {hasContent && (
              <div className="mt-16 text-center bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 border border-purple-100">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-2xl font-bold text-gray-900">
                      {generationBatches.length} 个作品批次
                    </p>
                    <p className="text-sm text-gray-600">
                      您已经开始了AI创作之旅
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleNavigationChange('gallery')}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg font-medium"
                >
                  <Image className="w-5 h-5" />
                  <span>查看我的作品画廊</span>
                  <span>→</span>
                </button>
              </div>
            )}
        </div>

        {/* 创作模式 - 生成过程中显示 */}
        <div className={`max-w-4xl mx-auto px-6 py-16 ${
          viewMode === 'create' ? 'block animate-fade-in' : 'hidden'
        }`}>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              AI正在为您创作中...
              </h2>
            </div>

            {/* 生成进度组件 */}
            <LoadingIndicator />
        </div>

        {/* 作品画廊模式 */}
        <div className={`max-w-7xl mx-auto px-6 py-8 ${
          viewMode === 'gallery' ? 'block animate-fade-in' : 'hidden'
        }`}>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">我的作品画廊</h2>
                {!isLoading && (
                  <p className="text-gray-600 mt-1">
                    共 {pagination.total} 个作品批次 | 已加载 {generationBatches.length} 个批次 (每页10个)，{generationHistory.length} 张图片
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleNavigationChange('home')}
                  className="inline-flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Plus className="w-4 h-4" />
                  <span>创作新作品</span>
                </button>
              </div>
            </div>

            {/* 画廊加载状态 */}
            {isGalleryLoading ? (
              <ImageGridSkeleton count={9} />
            ) : galleryError ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">加载失败</h3>
                <p className="text-gray-600 mb-6">{galleryError}</p>
                <button
                  onClick={() => {
                    setGalleryLoaded(false);
                    setGalleryError(null);
                  }}
                  className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>重新加载</span>
                </button>
              </div>
            ) : generationBatches.length > 0 ? (
              <>
                <ImageGrid viewMode="masonry" onRegenerate={handleRegenerate} />
                
                {/* 📄 分页控制 */}
                <div className="mt-12 text-center">
                  <div className="mb-6">
                                         <p className="text-gray-600 text-sm">
                       已显示 {generationBatches.length} 个批次，共 {pagination.total} 个批次
                       {pagination.totalPages > 1 && (
                         <span className="ml-2">
                           第 {pagination.currentPage} / {pagination.totalPages} 页 (每页10个批次)
                         </span>
                       )}
                     </p>
                  </div>
                  
                  {pagination.hasMore && (
                    <button
                      onClick={loadMoreHistory}
                      disabled={pagination.isLoadingMore}
                      className={`inline-flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                        pagination.isLoadingMore
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:shadow-lg'
                      }`}
                    >
                      {pagination.isLoadingMore ? (
                        <>
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                          <span>加载中...</span>
                        </>
                      ) : (
                        <>
                          <History className="w-4 h-4" />
                          <span>加载更多作品</span>
                        </>
                      )}
                    </button>
                  )}
                  
                  {!pagination.hasMore && pagination.total > 0 && (
                    <p className="text-gray-500 text-sm">
                      🎉 已显示全部作品
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Image className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">还没有作品</h3>
                <p className="text-gray-600 mb-6">
                  开始您的AI创作之旅，生成您的第一个作品吧！
                </p>
                <button
                  onClick={() => handleNavigationChange('home')}
                  className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  <span>开始创作</span>
                </button>
              </div>
            )}
        </div>
      </main>

      {/* 设置面板 */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => !currentGeneration.isGenerating && handleCloseSettings()}
        >
          <div
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] transform transition-all duration-300 scale-100 flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 固定标题栏 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">AI图像生成</h2>
              <button
                onClick={handleCloseSettings}
                disabled={currentGeneration.isGenerating}
                className={`p-2 rounded-xl transition-colors ${
                  currentGeneration.isGenerating
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 可滚动内容区域 */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <SettingsTabs
                initialPrompt={sidebarPrompt}
                suggestedTags={suggestedTags}
                parsedFeatures={currentConfig.parsedFeatures}
                disabled={currentGeneration.isGenerating}
                onPromptChange={setCurrentPrompt}
                onProcessingChange={setIsProcessing}
                selectedScenePackId={selectedScenePackId}
                onTabChange={setCurrentTab}
                activeTab={currentTab}
              />
            </div>

            {/* 统一的生成按钮（固定底部） */}
            <div className="flex-shrink-0">
              <GenerateButton
                prompt={currentPrompt}
                disabled={currentGeneration.isGenerating}
                isProcessing={isProcessing}
                currentTab={currentTab}
                selectedScenePackId={selectedScenePackId}
                onNext={() => setCurrentTab('prompt')}
              />
            </div>

            {/* 生成中遮罩层 */}
            {currentGeneration.isGenerating && (
              <div className="absolute inset-0 bg-white/98 z-10 rounded-2xl flex items-center justify-center">
                <LoadingIndicator />
              </div>
            )}

            {/* 生成完成预览 */}
            {currentGeneration.stage === 'completed' && !currentGeneration.isGenerating && (
              <div className="absolute inset-0 bg-white z-10 rounded-2xl flex flex-col p-6 overflow-y-auto">
                <div className="space-y-6">
                  {/* 成功图标动画 */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-bounce-once mb-3">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">✨ 生成完成！</h3>
                    <p className="text-sm text-gray-600">共生成 {generationBatches[0]?.results?.length || 0} 张图片</p>
                  </div>

                  {/* 图片缩略图预览 */}
                  {generationBatches.length > 0 && generationBatches[0].results && (
                    <div className="grid grid-cols-2 gap-3">
                      {generationBatches[0].results.slice(0, 4).map((result, index) => (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200 hover:border-purple-400 transition-all duration-300 group">
                          <img
                            src={result.imageUrl}
                            alt={`生成的图片 ${index + 1}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                            <span className="text-white text-xs font-medium">图片 {index + 1}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 操作按钮 */}
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => {
                        setViewMode('gallery');
                        setShowSettings(false);
                        setSidebarPrompt('');
                        setSuggestedTags(null);
                      }}
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-lg"
                    >
                      查看完整作品
                    </button>
                    <button
                      onClick={() => {
                        // 重置生成状态，允许继续创作
                        useAIGenerationStore.getState().resetGeneration();
                      }}
                      className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300"
                    >
                      继续创作
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 生成失败提示 */}
            {currentGeneration.stage === 'error' && !currentGeneration.isGenerating && (() => {
              const errorMessage = currentGeneration.error || '发生了未知错误';

              // 智能错误分析
              const getErrorInfo = () => {
                const lowerError = errorMessage.toLowerCase();

                // 网络错误
                if (lowerError.includes('network') || lowerError.includes('timeout') || lowerError.includes('fetch')) {
                  return {
                    icon: '🌐',
                    title: '网络连接问题',
                    reasons: [
                      '网络连接不稳定或超时',
                      'VPN或代理服务可能影响连接',
                      '防火墙阻止了请求'
                    ],
                    solutions: [
                      '检查网络连接状态',
                      '尝试关闭VPN后重试',
                      '等待几分钟后再次尝试'
                    ]
                  };
                }

                // 配额/限制错误
                if (lowerError.includes('quota') || lowerError.includes('limit') || lowerError.includes('达上限')) {
                  return {
                    icon: '⚠️',
                    title: '使用配额限制',
                    reasons: [
                      '今日生成次数已用完',
                      '账户配额不足',
                      '服务器限流保护'
                    ],
                    solutions: [
                      '等待明日凌晨配额重置',
                      '查看账户配额情况',
                      '联系客服升级配额'
                    ]
                  };
                }

                // 内容审核错误
                if (lowerError.includes('content') || lowerError.includes('inappropriate') || lowerError.includes('敏感')) {
                  return {
                    icon: '🚫',
                    title: '内容审核未通过',
                    reasons: [
                      '提示词包含敏感或违规内容',
                      '图像主题不符合平台规范',
                      '触发了安全过滤规则'
                    ],
                    solutions: [
                      '修改提示词，避免敏感词汇',
                      '使用更加明确和正面的描述',
                      '参考平台内容规范'
                    ]
                  };
                }

                // 服务器错误
                if (lowerError.includes('500') || lowerError.includes('503') || lowerError.includes('server')) {
                  return {
                    icon: '🔧',
                    title: '服务器暂时不可用',
                    reasons: [
                      'AI服务器正在维护',
                      '服务器负载过高',
                      '后端服务临时故障'
                    ],
                    solutions: [
                      '等待5-10分钟后重试',
                      '选择非高峰时段使用',
                      '关注平台公告了解维护信息'
                    ]
                  };
                }

                // 默认通用错误
                return {
                  icon: '❌',
                  title: '生成失败',
                  reasons: [
                    '服务器繁忙，请稍后重试',
                    '生成参数可能存在问题',
                    '临时网络波动'
                  ],
                  solutions: [
                    '稍后再次尝试',
                    '检查提示词和参数设置',
                    '联系客服获取帮助'
                  ]
                };
              };

              const errorInfo = getErrorInfo();

              return (
                <div className="absolute inset-0 bg-white z-10 rounded-2xl flex flex-col p-6 overflow-y-auto">
                  <div className="space-y-6 max-w-md mx-auto w-full">
                    {/* 错误图标 */}
                    <div className="flex flex-col items-center">
                      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-4xl mb-3">
                        {errorInfo.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{errorInfo.title}</h3>
                      <p className="text-sm text-gray-600 text-center px-4">{errorMessage}</p>
                    </div>

                    {/* 可能原因 */}
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="font-medium text-red-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        可能的原因
                      </p>
                      <ul className="space-y-1 text-sm text-red-800">
                        {errorInfo.reasons.map((reason, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 解决方案 */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="font-medium text-blue-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        解决方案
                      </p>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {errorInfo.solutions.map((solution, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">✓</span>
                            <span>{solution}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex flex-col space-y-3">
                      <button
                        onClick={() => {
                          // 重置生成状态，保留设置
                          useAIGenerationStore.getState().resetGeneration();
                        }}
                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-lg"
                      >
                        重新生成
                      </button>
                      <button
                        onClick={() => {
                          useAIGenerationStore.getState().resetGeneration();
                          setShowSettings(false);
                        }}
                        className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300"
                      >
                        关闭
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 右下角快捷按钮 - 仅在首页显示 */}
      {viewMode === 'home' && (
        <button
          onClick={handleFloatingButtonClick}
          className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-40 transform hover:scale-110 active:scale-95 animate-bounce-subtle"
          title="快速创作"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Toast 通知容器 */}
      <ToastContainer toasts={toast.toasts} onClose={toast.close} />
    </div>
  );
}

export default App; 